import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyEmailToken } from '../services/authService';
import { useAuth } from '../context/AuthContext';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setState('error');
      setErrorMsg('No verification token found in the link.');
      return;
    }
    verifyEmailToken(token)
      .then(() => {
        setState('success');
        let t = 3;
        setCountdown(t);
        const iv = setInterval(() => {
          t -= 1;
          setCountdown(t);
          if (t <= 0) { clearInterval(iv); navigate(user ? '/dashboard' : '/auth'); }
        }, 1000);
      })
      .catch(err => {
        setErrorMsg(err.message || 'This link is invalid or has expired.');
        setState('error');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      minHeight: '100vh', background: '#0f172a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Inter, system-ui, sans-serif', padding: '24px',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background grid */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(to right,#22D3EE 1px,transparent 1px),linear-gradient(to bottom,#22D3EE 1px,transparent 1px)',
        backgroundSize: '64px 64px' }} />
      {/* Glow */}
      <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translateX(-50%)',
        width: 500, height: 300, pointerEvents: 'none',
        background: 'radial-gradient(ellipse,rgba(71,158,195,0.1) 0%,transparent 70%)' }} />

      <div style={{
        position: 'relative', zIndex: 1,
        background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(20px)',
        border: '1px solid rgba(71,158,195,0.2)', borderRadius: 20,
        padding: '48px 44px', maxWidth: 440, width: '100%', textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>

        {state === 'loading' && (
          <>
            <div style={{ width: 52, height: 52, border: '3px solid rgba(71,158,195,0.2)',
              borderTop: '3px solid #479EC3', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 24px' }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>
              Verifying your email…
            </h2>
            <p style={{ fontSize: 14, color: '#64748b' }}>Just a moment, please.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 24px',
              background: 'rgba(34,211,238,0.12)', border: '1px solid rgba(34,211,238,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="26" height="26" fill="none" stroke="#22D3EE" strokeWidth="2.2" viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', marginBottom: 10, letterSpacing: '-0.02em' }}>
              Email verified!
            </h2>
            <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.65, marginBottom: 20 }}>
              Your email address has been confirmed.
            </p>
            <p style={{ fontSize: 13.5, color: '#475569' }}>
              Redirecting in <span style={{ color: '#22D3EE', fontWeight: 700 }}>{countdown}</span>…
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 24px',
              background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="24" height="24" fill="none" stroke="#f87171" strokeWidth="2.2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeLinecap="round"/>
                <line x1="12" y1="8" x2="12" y2="12" strokeLinecap="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', marginBottom: 10, letterSpacing: '-0.02em' }}>
              Link invalid or expired
            </h2>
            <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.65, marginBottom: 8 }}>
              {errorMsg}
            </p>
            <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 32 }}>
              Verification links expire after 10 minutes. Sign in to your account and we'll send you a new one.
            </p>
            <button
              onClick={() => navigate('/auth')}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                color: '#e2e8f0', fontSize: 15, fontWeight: 600,
              }}>
              Back to Sign In
            </button>
          </>
        )}

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
